import { ArrowLeft, History, Loader2, Minus, Package, Percent, Plus, Rocket, Save, Wand2 } from 'lucide-react';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Line, LineChart, Tooltip as RechartsTooltip, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import Header from '../components/Header';
import { ProductThumbnail } from '../components/ui/aspect-ratio-image';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { MessageDialog } from '../components/ui/alert-dialog-helper';
import { isAdmin } from '../lib/roles';
import { cn } from '../lib/utils';

const inputNumberNoSpin =
  '[-moz-appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface MarginTier {
  duration_months: number;
  margin_percentage: number;
}

interface SupplierProduct {
  id: string;
  name: string;
  base_price: number | null;
  cost_price: number | null;
  is_active: boolean;
  images?: Array<{ id: string; url: string | null; storage_key: string | null; variant: string }>;
}

interface SupplierData {
  id: string;
  name: string;
  is_active: boolean;
  notes: string | null;
  markup_percentage: number | null;
  margin_tiers: MarginTier[];
  products: SupplierProduct[];
}

interface PricingEventRow {
  id: string;
  status: string;
  created_at: string;
  completed_at: string | null;
  templates_recalculated: number;
  templates_skipped: number;
  templates_failed: number;
  templates_total: number;
  actor_user?: { email?: string } | null;
}

interface MarginSnapshot {
  markup_percentage: number | null;
  tiers: Array<{ duration_months: number; margin_percentage: number }>;
}

interface RentalPriceSnap {
  duration_count: number;
  duration_unit: string;
  total_price: number;
  name: string;
}

const COMPARE_MARGIN_MO = [1, 3, 6, 12, 24, 36];

const marginPctMap = (tiers: MarginSnapshot['tiers'] | null | undefined) => {
  const m = new Map<number, number>();
  for (const t of tiers || []) m.set(t.duration_months, t.margin_percentage);
  return m;
};

const totalAtMonths = (rows: RentalPriceSnap[] | null | undefined, mo: number): number | null => {
  if (!rows?.length) return null;
  const hit = rows.find(r => r.duration_unit === 'months' && r.duration_count === mo);
  return hit != null ? hit.total_price : null;
};

const MARGIN_DURATIONS = Array.from({ length: 36 }, (_, i) => i + 1);
const PLANNER_DURATIONS = [1, 2, 3, 6, 12, 24, 36];
const MAX_BREAK_EVEN_TARGET_MO = 36;
const MAX_MARGIN_MO = 36;
const DISCOUNT_MIN = 0.05;
const DISCOUNT_MAX = 1;
const DISCOUNT_STEP = 0.05;
const token = () => localStorage.getItem('token');

const snapPlannerValue = (v: number, min: number, max: number, step: number) => {
  const c = Math.min(max, Math.max(min, v));
  const k = Math.round((c - min) / step);
  const s = min + k * step;
  const dec = Math.max(0, (String(step).split('.')[1]?.length ?? 0));
  return Math.min(max, Math.max(min, Number(s.toFixed(dec))));
};

/** Best p so 100*(d/target)^p ≈ current tier margins (PLANNER_DURATIONS); snapped to slider step. */
const fitCurvePowerToMargins = (
  targetMonths: number,
  marginsMap: Record<number, string>,
  durations: readonly number[] = PLANNER_DURATIONS,
) => {
  const target = Math.min(MAX_BREAK_EVEN_TARGET_MO, Math.max(1, Math.round(targetMonths)));
  let sumXY = 0;
  let sumXX = 0;
  for (const d of durations) {
    const raw = marginsMap[d];
    const c = raw !== undefined && raw !== '' ? parseFloat(raw) : NaN;
    if (!Number.isFinite(c) || c <= 0) continue;
    const ratio = d / target;
    if (ratio <= 0 || Math.abs(Math.log(ratio)) < 1e-8) continue;
    const x = Math.log(ratio);
    const y = Math.log(c / 100);
    if (!Number.isFinite(y)) continue;
    sumXY += x * y;
    sumXX += x * x;
  }
  if (sumXX < 1e-12) return snapPlannerValue(0.5, DISCOUNT_MIN, DISCOUNT_MAX, DISCOUNT_STEP);
  const pRaw = sumXY / sumXX;
  const p = Number.isFinite(pRaw) ? pRaw : 0.5;
  return snapPlannerValue(Math.min(DISCOUNT_MAX, Math.max(DISCOUNT_MIN, p)), DISCOUNT_MIN, DISCOUNT_MAX, DISCOUNT_STEP);
};

/** Matches server `computePlannerMarginTiers` (1..36 mo, ceil). */
const computePlannerMarginTiersLocal = (targetMonths: number, curvePower: number): Record<number, string> => {
  const target = Math.min(MAX_MARGIN_MO, Math.max(1, Math.round(targetMonths)));
  const power = Number.isFinite(curvePower) && curvePower > 0 ? curvePower : 0.5;
  const next: Record<number, string> = {};
  for (let dur = 1; dur <= MAX_MARGIN_MO; dur++) {
    next[dur] = String(Math.ceil(100 * Math.pow(dur / target, power)));
  }
  return next;
};

const fmtCurveTooltip = (v: number) => String(parseFloat(v.toFixed(2)));
const fmt = (v: number) => {
  const neg = v < 0;
  const s = Math.abs(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return neg ? `-£${s}` : `£${s}`;
};

const PlannerSliderTrack = memo(function PlannerSliderTrack({
  min,
  max,
  step,
  value,
  onValueChange,
  ariaLabel,
  ariaValueText,
  tooltipText,
  startLabel,
  endLabel,
}: {
  min: number;
  max: number;
  step: number;
  value: number;
  onValueChange: (n: number) => void;
  ariaLabel: string;
  ariaValueText: string;
  tooltipText: string;
  startLabel: string;
  endLabel: string;
}) {
  const span = max - min;
  const pct = span <= 0 ? 0 : ((value - min) / span) * 100;
  const snap = (n: number) => snapPlannerValue(n, min, max, step);
  const dec = () => onValueChange(snap(value - step));
  const inc = () => onValueChange(snap(value + step));
  const atMin = value <= min + 1e-9;
  const atMax = value >= max - 1e-9;
  return (
    <div className="w-full min-w-0">
      <div className="flex w-full min-w-0 items-end gap-2 sm:gap-3">
        <span className="w-12 shrink-0 pb-2 text-right text-xs font-medium tabular-nums text-muted-foreground">
          {startLabel}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          disabled={atMin}
          onClick={dec}
          aria-label="Decrease"
        >
          <Minus className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="relative h-7 w-full">
            <span
              className="pointer-events-none absolute bottom-0 z-10 rounded-lg border-2 border-primary/50 bg-primary px-2.5 py-0.5 text-xs font-semibold tabular-nums text-primary-foreground shadow-lg whitespace-nowrap ring-2 ring-ring/60 sm:px-3 sm:py-1 sm:text-sm"
              style={{ left: `${pct}%`, transform: 'translateX(-50%)' }}
              role="status"
              aria-live="polite"
            >
              {tooltipText}
            </span>
          </div>
          <input
            type="range"
            min={min}
            max={max}
            step={step}
            value={value}
            onChange={e => onValueChange(snap(Number(e.target.value)))}
            className="mt-1 h-2.5 w-full cursor-pointer accent-primary"
            aria-label={ariaLabel}
            aria-valuetext={ariaValueText}
          />
        </div>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8 shrink-0"
          disabled={atMax}
          onClick={inc}
          aria-label="Increase"
        >
          <Plus className="h-4 w-4" />
        </Button>
        <span className="w-12 shrink-0 pb-2 text-left text-xs font-medium tabular-nums text-muted-foreground">
          {endLabel}
        </span>
      </div>
    </div>
  );
});

const MarginTierRow = memo(function MarginTierRow({
  dur,
  marginStr,
  sampleCost,
  canEditMargins,
  onMarginChange,
}: {
  dur: number;
  marginStr: string;
  sampleCost: number;
  canEditMargins: boolean;
  onMarginChange: (d: number, v: string) => void;
}) {
  const marginVal = parseFloat(marginStr || '0');
  const total = sampleCost > 0 && marginVal > 0 ? (marginVal / 100) * sampleCost : 0;
  const monthly = dur > 0 ? total / dur : 0;
  const profitable = total > 0 && total >= sampleCost;
  return (
    <tr className="border-b last:border-0">
      <td className="py-1.5 pr-3 pl-4">{dur} mo</td>
      <td className="py-1.5 pr-3 min-w-[7rem] w-[7rem]">
        <Input
          type="number"
          step="0.01"
          value={marginStr}
          onChange={e => onMarginChange(dur, e.target.value)}
          placeholder="0"
          className={cn(
            'h-7 w-full min-w-[6.5rem] px-1.5 text-center text-sm tabular-nums',
            inputNumberNoSpin,
          )}
          disabled={!canEditMargins}
        />
      </td>
      <td className="py-1.5 pr-3 text-right font-mono">
        {marginVal > 0 ? fmt(monthly) : '—'}
      </td>
      <td className={cn(
        'py-1.5 pr-3 text-right font-mono',
        marginVal > 0 && profitable && 'text-green-600 dark:text-green-400 font-semibold',
        marginVal > 0 && !profitable && 'text-muted-foreground',
      )}>
        {marginVal > 0 ? fmt(total) : '—'}
      </td>
    </tr>
  );
});

function PublishSnapshotPanel({
  loading,
  detail,
  navigate,
  canEditMargins,
  onApplyPrevious,
}: {
  loading: boolean;
  detail: { event: Record<string, unknown>; template_rows: Record<string, unknown>[] } | null;
  navigate: ReturnType<typeof useNavigate>;
  canEditMargins: boolean;
  onApplyPrevious: () => void;
}) {
  if (loading) return <p className="text-sm text-muted-foreground">Loading snapshot…</p>;
  if (!detail) return <p className="text-sm text-destructive">Could not load this publish.</p>;
  const ev = detail.event;
  const pc = ev.planner_context as { target_months?: number; curve_power?: number } | null | undefined;
  const beforeM: MarginSnapshot = (ev.margin_snapshot_before as MarginSnapshot | null) ?? {
    markup_percentage: null,
    tiers: (ev.tiers_before as MarginSnapshot['tiers']) ?? [],
  };
  const afterM: MarginSnapshot = (ev.margin_snapshot_after as MarginSnapshot | null) ?? {
    markup_percentage: null,
    tiers: (ev.tiers_after as MarginSnapshot['tiers']) ?? [],
  };
  const bMap = marginPctMap(beforeM.tiers);
  const aMap = marginPctMap(afterM.tiers);
  const prevTiers = (ev.margin_snapshot_before as MarginSnapshot | null)?.tiers
    ?? (ev.tiers_before as MarginSnapshot['tiers']);
  const hasPreviousMargins = Array.isArray(prevTiers) && prevTiers.length > 0;
  return (
    <div className="space-y-6">
      {pc && (pc.target_months != null || pc.curve_power != null) && (
        <p className="text-sm text-muted-foreground">
          Planner at publish: break-even target {pc.target_months ?? '—'} mo · curve {pc.curve_power ?? '—'}
        </p>
      )}
      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Margins before</CardTitle>
            <CardDescription>Markup {beforeM.markup_percentage != null ? `${beforeM.markup_percentage}%` : '—'}</CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm border rounded-md">
              <thead><tr className="border-b bg-muted/40"><th className="text-left p-2">Mo</th><th className="text-right p-2">%</th></tr></thead>
              <tbody>
                {COMPARE_MARGIN_MO.map(mo => (
                  <tr key={mo} className="border-b last:border-0">
                    <td className="p-2">{mo}</td>
                    <td className="p-2 text-right font-mono tabular-nums">{bMap.has(mo) ? `${bMap.get(mo)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Margins after</CardTitle>
            <CardDescription>Markup {afterM.markup_percentage != null ? `${afterM.markup_percentage}%` : '—'}</CardDescription>
          </CardHeader>
          <CardContent>
            <table className="w-full text-sm border rounded-md">
              <thead><tr className="border-b bg-muted/40"><th className="text-left p-2">Mo</th><th className="text-right p-2">%</th></tr></thead>
              <tbody>
                {COMPARE_MARGIN_MO.map(mo => (
                  <tr key={mo} className="border-b last:border-0">
                    <td className="p-2">{mo}</td>
                    <td className="p-2 text-right font-mono tabular-nums">{aMap.has(mo) ? `${aMap.get(mo)}%` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Products — calendar total rent</CardTitle>
          <CardDescription>Per-product snapshots from this publish run</CardDescription>
        </CardHeader>
        <CardContent>
          {detail.template_rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No per-product rows yet (job may still be running, or legacy publish).
            </p>
          ) : (
            <div className="overflow-x-auto max-h-[min(70vh,900px)] overflow-y-auto border rounded-md">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background border-b z-10">
                  <tr>
                    <th className="text-left p-2">Product</th>
                    <th className="text-left p-2">Result</th>
                    <th className="text-right p-2">12 mo before</th>
                    <th className="text-right p-2">12 mo after</th>
                    <th className="text-right p-2">Δ</th>
                  </tr>
                </thead>
                <tbody>
                  {detail.template_rows.map(row => {
                    const at = row.asset_template as { id?: string; name?: string } | undefined;
                    const name = at?.name ?? '—';
                    const tid = at?.id ?? (row.asset_template_id as string);
                    const ob = row.rental_pricing_before as RentalPriceSnap[] | null;
                    const oa = row.rental_pricing_after as RentalPriceSnap[] | null;
                    const p0 = totalAtMonths(ob, 12);
                    const p1 = totalAtMonths(oa, 12);
                    const delta = p0 != null && p1 != null ? Math.round((p1 - p0) * 100) / 100 : null;
                    const months = new Set<number>();
                    for (const x of ob || []) if (x.duration_unit === 'months') months.add(x.duration_count);
                    for (const x of oa || []) if (x.duration_unit === 'months') months.add(x.duration_count);
                    const sortedM = Array.from(months).sort((a, b) => a - b);
                    return (
                      <tr key={String(row.id)} className="border-b last:border-0 align-top">
                        <td className="p-2">
                          <button
                            type="button"
                            className="text-left text-primary hover:underline"
                            onClick={() => navigate(`/assets/${tid}`)}
                          >
                            {name}
                          </button>
                          <details className="mt-1 text-xs text-muted-foreground">
                            <summary className="cursor-pointer select-none">All durations</summary>
                            <table className="mt-1 w-full font-mono tabular-nums">
                              <thead>
                                <tr className="border-b">
                                  <th className="text-left py-0.5">Mo</th>
                                  <th className="text-right py-0.5">Before</th>
                                  <th className="text-right py-0.5">After</th>
                                  <th className="text-right py-0.5">Δ</th>
                                </tr>
                              </thead>
                              <tbody>
                                {sortedM.map(mo => {
                                  const vb = totalAtMonths(ob, mo);
                                  const va = totalAtMonths(oa, mo);
                                  const d =
                                    vb != null && va != null ? Math.round((va - vb) * 100) / 100 : null;
                                  return (
                                    <tr key={mo}>
                                      <td className="py-0.5">{mo}</td>
                                      <td className="py-0.5 text-right">{vb != null ? fmt(vb) : '—'}</td>
                                      <td className="py-0.5 text-right">{va != null ? fmt(va) : '—'}</td>
                                      <td className="py-0.5 text-right">
                                        {d != null ? `${d >= 0 ? '+' : ''}${fmt(d)}` : '—'}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </details>
                        </td>
                        <td className="p-2">
                          <Badge variant="secondary">{String(row.outcome)}</Badge>
                          {row.skip_reason != null && String(row.skip_reason) !== '' && (
                            <span className="block text-muted-foreground mt-0.5 text-xs">{String(row.skip_reason)}</span>
                          )}
                        </td>
                        <td className="p-2 text-right font-mono">{p0 != null ? fmt(p0) : '—'}</td>
                        <td className="p-2 text-right font-mono">{p1 != null ? fmt(p1) : '—'}</td>
                        <td className="p-2 text-right font-mono">{delta != null ? `${delta >= 0 ? '+' : ''}${fmt(delta)}` : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
      {canEditMargins && (
        <div className="flex flex-wrap gap-2 pt-2 border-t">
          <Button type="button" variant="secondary" onClick={onApplyPrevious} disabled={!hasPreviousMargins}>
            Apply previous margins to calculator (Margins tab)
          </Button>
        </div>
      )}
    </div>
  );
}

export default function SupplierDetails() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);
  const [supplier, setSupplier] = useState<SupplierData | null>(null);
  const [loading, setLoading] = useState(true);
  const [margins, setMargins] = useState<Record<number, string>>({});
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [resultDialog, setResultDialog] = useState<{
    open: boolean;
    title: string;
    description: string;
    destructive?: boolean;
  }>({ open: false, title: '', description: '' });
  const [pricingEvents, setPricingEvents] = useState<PricingEventRow[]>([]);
  const [livePublish, setLivePublish] = useState<Record<string, unknown> | null>(null);
  const [tab, setTab] = useState<'margins' | 'history' | 'products'>(() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      return isAdmin(u) ? 'margins' : 'products';
    } catch {
      return 'products';
    }
  });
  const [selectedHistoryEventId, setSelectedHistoryEventId] = useState<string | null>(null);
  const historySnapshotRef = useRef<HTMLDivElement | null>(null);
  const [historyDetailLoading, setHistoryDetailLoading] = useState(false);
  const [historyDetail, setHistoryDetail] = useState<{
    event: Record<string, unknown>;
    template_rows: Record<string, unknown>[];
  } | null>(null);
  const [markupPct, setMarkupPct] = useState('');
  const [sampleCost, setSampleCost] = useState<number>(0);
  const [productSearch, setProductSearch] = useState('');
  const [productPage, setProductPage] = useState(1);
  const [targetBreakEven, setTargetBreakEven] = useState('12');
  const [curvePower, setCurvePower] = useState('0.5');
  const PRODUCTS_PER_PAGE = 15;
  const targetBreakEvenRef = useRef(targetBreakEven);
  targetBreakEvenRef.current = targetBreakEven;

  const loadSupplier = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const res = await fetch(`/api/suppliers/${id}`, { headers: { Authorization: `Bearer ${token()}` } });
      if (!res.ok) throw new Error('Failed to load supplier');
      const data = await res.json();
      const s = data.data as SupplierData;
      setSupplier(s);

      const m: Record<number, string> = {};
      for (const tier of s.margin_tiers) m[tier.duration_months] = tier.margin_percentage.toString();
      setMargins(m);
      setMarkupPct(s.markup_percentage?.toString() || '');

      const firstWithCost = s.products.find(p => p.cost_price && Number(p.cost_price) > 0);
      setSampleCost(firstWithCost ? Number(firstWithCost.cost_price) : 1000);

      const t = Math.min(MAX_BREAK_EVEN_TARGET_MO, Math.max(1, parseInt(targetBreakEvenRef.current, 10) || 12));
      setCurvePower(String(fitCurvePowerToMargins(t, m)));
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadPricingEvents = useCallback(async () => {
    if (!id) return;
    try {
      const res = await fetch(`/api/suppliers/${id}/pricing-events?limit=20`, {
        headers: { Authorization: `Bearer ${token()}` },
      });
      if (!res.ok) return;
      const j = await res.json();
      if (j.success && Array.isArray(j.data)) setPricingEvents(j.data);
    } catch {
      /* ignore */
    }
  }, [id]);

  useEffect(() => {
    const savedUser = localStorage.getItem('user');
    if (!token() || !savedUser) { navigate('/login'); return; }
    try { setUser(JSON.parse(savedUser)); } catch { navigate('/login'); }
    loadSupplier();
  }, [loadSupplier]);

  useEffect(() => {
    if (!id || !supplier || supplier.id !== id || loading) return;
    const t = Math.min(MAX_BREAK_EVEN_TARGET_MO, Math.max(1, parseInt(targetBreakEven, 10) || 12));
    setCurvePower(String(fitCurvePowerToMargins(t, margins)));
  }, [targetBreakEven]);

  useEffect(() => {
    if (!id || !isAdmin(user)) return;
    void loadPricingEvents();
  }, [id, loadPricingEvents, user]);

  useEffect(() => {
    setSelectedHistoryEventId(null);
    setHistoryDetail(null);
  }, [id]);

  useEffect(() => {
    if (!id || !selectedHistoryEventId || !isAdmin(user)) {
      setHistoryDetail(null);
      setHistoryDetailLoading(false);
      return;
    }
    let cancelled = false;
    setHistoryDetailLoading(true);
    setHistoryDetail(null);
    void (async () => {
      try {
        const res = await fetch(`/api/suppliers/${id}/pricing-publish/${selectedHistoryEventId}/detail`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        const j = await res.json();
        if (!cancelled && j.success && j.data) setHistoryDetail(j.data);
      } catch {
        if (!cancelled) setHistoryDetail(null);
      } finally {
        if (!cancelled) setHistoryDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, selectedHistoryEventId, user]);

  useLayoutEffect(() => {
    if (!selectedHistoryEventId || !isAdmin(user)) return;
    const el = historySnapshotRef.current;
    if (!el) return;
    const root = document.scrollingElement ?? document.documentElement;
    const y = el.getBoundingClientRect().top + root.scrollTop - 8;
    root.scrollTo({ top: Math.max(0, y), behavior: 'auto' });
  }, [selectedHistoryEventId, historyDetailLoading, user]);

  useEffect(() => {
    if (user && !isAdmin(user)) {
      setTab('products');
      setSelectedHistoryEventId(null);
    }
  }, [user]);

  const applyPreviousMarginsFromHistory = useCallback(() => {
    const ev = historyDetail?.event;
    if (!ev) return;
    const snap = ev.margin_snapshot_before as MarginSnapshot | null | undefined;
    const tiers = snap?.tiers ?? (ev.tiers_before as MarginSnapshot['tiers'] | undefined) ?? [];
    if (!tiers.length) return;
    const next: Record<number, string> = {};
    for (const d of MARGIN_DURATIONS) next[d] = '';
    for (const t of tiers) next[t.duration_months] = String(t.margin_percentage);
    setMargins(next);
    const mk = snap?.markup_percentage;
    if (mk != null && Number.isFinite(Number(mk))) setMarkupPct(String(mk));
    setTab('margins');
  }, [historyDetail]);

  const canEditMargins = isAdmin(user);
  const markupN = parseFloat(markupPct) || 0;
  const breakEvenTargetMo = Math.min(MAX_BREAK_EVEN_TARGET_MO, Math.max(1, parseInt(targetBreakEven, 10) || 12));
  const curvePowerEffective = useMemo(
    () => snapPlannerValue(parseFloat(curvePower) || 0.5, DISCOUNT_MIN, DISCOUNT_MAX, DISCOUNT_STEP),
    [curvePower],
  );

  const profitabilityPlan = useMemo(() => {
    const cost = sampleCost;
    if (cost <= 0) return null;
    const target = breakEvenTargetMo;
    const power = curvePowerEffective;
    return PLANNER_DURATIONS.map(dur => {
      const requiredMarginPct = 100 * Math.pow(dur / target, power);
      const requiredMonthly = (requiredMarginPct / 100) * cost / dur;
      const totalRevenue = requiredMonthly * dur;
      const profitAtEnd = totalRevenue - cost;
      const raw = margins[dur];
      const parsed = raw !== undefined && raw !== '' ? parseFloat(raw) : NaN;
      const currentMargin = Number.isFinite(parsed) ? parsed : null;
      const roundedRequired = Math.round(requiredMarginPct * 100) / 100;
      return {
        duration: dur,
        required_margin: roundedRequired,
        monthly_price: Math.round(requiredMonthly * 100) / 100,
        total_revenue: Math.round(totalRevenue * 100) / 100,
        profit_at_end: Math.round(profitAtEnd * 100) / 100,
        current_margin: currentMargin,
      };
    });
  }, [sampleCost, breakEvenTargetMo, curvePowerEffective, margins]);

  const publishPreview = useMemo(() => {
    if (!supplier) {
      return {
        eligible: [] as SupplierProduct[],
        skipped: [] as Array<{ product: SupplierProduct; reason: string }>,
        markupMismatch: false,
      };
    }
    const hasPositiveTier = MARGIN_DURATIONS.some(d => {
      const raw = margins[d] ?? '';
      const v = parseFloat(raw);
      return Number.isFinite(v) && v > 0;
    });
    const markupSaved = Number(supplier.markup_percentage ?? 0);
    const eligible: SupplierProduct[] = [];
    const skipped: Array<{ product: SupplierProduct; reason: string }> = [];
    if (!hasPositiveTier) return { eligible, skipped, markupMismatch: false };
    for (const p of supplier.products) {
      const cost = Number(p.cost_price || 0);
      if (cost <= 0) {
        skipped.push({ product: p, reason: 'No cost price' });
        continue;
      }
      const rrp = markupSaved > 0 ? cost * (1 + markupSaved / 100) : Number(p.base_price || 0);
      if (rrp <= 0) {
        skipped.push({
          product: p,
          reason: markupSaved > 0 ? 'RRP would not be positive' : 'No base price while markup % is 0',
        });
        continue;
      }
      eligible.push(p);
    }
    const formMk = parseFloat(markupPct) || 0;
    const markupMismatch = Math.abs(formMk - markupSaved) > 1e-6;
    return { eligible, skipped, markupMismatch };
  }, [supplier, margins, markupPct]);

  const onMarginChange = useCallback((d: number, v: string) => {
    setMargins(prev => ({ ...prev, [d]: v }));
  }, []);

  const saveMargins = async () => {
    setSaving(true);
    try {
      await fetch(`/api/suppliers/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ markup_percentage: parseFloat(markupPct) || null }),
      });

      const tiers = Object.entries(margins)
        .filter(([, v]) => v && parseFloat(v) > 0)
        .map(([k, v]) => ({ duration_months: parseInt(k), margin_percentage: parseFloat(v) }));

      const res = await fetch(`/api/suppliers/${id}/margins`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ tiers }),
      });
      if (res.ok) await loadSupplier();
    } catch (err) {
      console.error('Error saving margins:', err);
    } finally {
      setSaving(false);
    }
  };

  const applyPlannerToMargins = () => {
    setMargins(computePlannerMarginTiersLocal(breakEvenTargetMo, curvePowerEffective));
  };

  const tiersPayloadForPublish = useCallback(
    () =>
      MARGIN_DURATIONS.map(dur => {
        const raw = margins[dur] ?? '';
        const v = parseFloat(raw);
        return { duration_months: dur, margin_percentage: Number.isFinite(v) && v >= 0 ? v : 0 };
      }),
    [margins],
  );

  const pollPublishUntilDone = useCallback(
    async (eventId: string) => {
      for (;;) {
        const res = await fetch(`/api/suppliers/${id}/pricing-publish/${eventId}`, {
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (!res.ok) throw new Error('Could not read publish status');
        const j = await res.json();
        const ev = j.data as Record<string, unknown> & { status?: string; error_message?: string };
        setLivePublish(ev);
        const st = ev?.status;
        if (st === 'completed') {
          const rec = ev?.templates_recalculated ?? 0;
          setResultDialog({
            open: true,
            title: 'Publish complete',
            description: `Rental pricing recalculated for ${rec} product(s). Shopify sync is not included yet.`,
          });
          setTab('history');
          setSelectedHistoryEventId(eventId);
          return;
        }
        if (st === 'failed') {
          setResultDialog({
            open: true,
            title: 'Publish failed',
            description: String(ev?.error_message || 'Unknown error'),
            destructive: true,
          });
          return;
        }
        await new Promise(r => setTimeout(r, 3000));
      }
    },
    [id],
  );

  const openPublishConfirm = useCallback(() => {
    if (!tiersPayloadForPublish().some(t => t.margin_percentage > 0)) {
      setResultDialog({
        open: true,
        title: 'Cannot publish',
        description: 'At least one margin tier must be greater than 0.',
        destructive: true,
      });
      return;
    }
    setPublishConfirmOpen(true);
  }, [tiersPayloadForPublish]);

  const executePublish = useCallback(async () => {
    const tiers = tiersPayloadForPublish();
    setPublishConfirmOpen(false);
    setPublishing(true);
    setLivePublish(null);
    try {
      const res = await fetch(`/api/suppliers/${id}/publish-pricing`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body: JSON.stringify({
          tiers,
          planner_context: { target_months: breakEvenTargetMo, curve_power: curvePowerEffective },
        }),
      });
      const body = await res.json().catch(() => ({}));
      if (res.status === 409) {
        setResultDialog({
          open: true,
          title: 'Publish blocked',
          description: String(body.error || 'A publish is already running for this supplier.'),
          destructive: true,
        });
        return;
      }
      if (!res.ok) {
        setResultDialog({
          open: true,
          title: 'Publish failed',
          description: String(body.error || 'Publish failed to start'),
          destructive: true,
        });
        return;
      }
      const eventId = body.data?.event_id as string | undefined;
      if (!eventId) {
        setResultDialog({
          open: true,
          title: 'Publish error',
          description: 'No event id returned from server.',
          destructive: true,
        });
        return;
      }
      await pollPublishUntilDone(eventId);
      await loadSupplier();
      await loadPricingEvents();
    } catch (e) {
      console.error(e);
      setResultDialog({
        open: true,
        title: 'Publish error',
        description: e instanceof Error ? e.message : 'Unknown error',
        destructive: true,
      });
    } finally {
      setPublishing(false);
    }
  }, [
    id,
    tiersPayloadForPublish,
    breakEvenTargetMo,
    curvePowerEffective,
    loadSupplier,
    loadPricingEvents,
    pollPublishUntilDone,
  ]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="suppliers" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-8">Loading supplier...</div>
        </main>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="min-h-screen bg-background">
        <Header user={user} currentPage="suppliers" />
        <main className="container mx-auto px-4 py-8">
          <div className="text-center py-8 text-red-600">Supplier not found</div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="suppliers" />
      <main className="container mx-auto px-4 py-8">
        <button onClick={() => navigate('/suppliers')} className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-3">
          <ArrowLeft className="h-4 w-4 mr-2" /> Back to Suppliers
        </button>

        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold">{supplier.name}</h1>
          <div className="flex items-center gap-3 mt-2">
            <Badge variant={supplier.is_active ? 'default' : 'secondary'}>
              {supplier.is_active ? 'Active' : 'Inactive'}
            </Badge>
            <span className="text-sm text-muted-foreground">{supplier.products.length} products</span>
          </div>
          {supplier.notes && <p className="text-sm text-muted-foreground mt-2">{supplier.notes}</p>}
        </div>

        {(canEditMargins && publishing && livePublish) && (
          <Card className="mb-8 border-primary/30">
            <CardHeader>
              <CardTitle className="text-base">Publish in progress</CardTitle>
              <CardDescription>
                Status: {(livePublish.status as string) || '…'}
                {typeof livePublish.templates_processed === 'number' && typeof livePublish.templates_total === 'number'
                  ? ` · ${livePublish.templates_processed}/${livePublish.templates_total} products processed`
                  : ''}
              </CardDescription>
            </CardHeader>
          </Card>
        )}

        <Tabs value={tab} onValueChange={v => setTab(v as 'margins' | 'history' | 'products')} className="w-full">
          <TabsList
            className={cn(
              'mb-6 grid h-auto w-full p-1',
              canEditMargins ? 'grid-cols-3' : 'grid-cols-1',
            )}
          >
            {canEditMargins && (
              <>
                <TabsTrigger value="margins" className="flex items-center justify-center gap-2 py-2.5 text-xs sm:text-sm">
                  <Percent className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Margins & planner</span>
                  <span className="sm:hidden">Margins</span>
                </TabsTrigger>
                <TabsTrigger value="history" className="flex items-center justify-center gap-2 py-2.5 text-xs sm:text-sm">
                  <History className="h-4 w-4 shrink-0" />
                  <span className="hidden sm:inline">Publish history</span>
                  <span className="sm:hidden">History</span>
                </TabsTrigger>
              </>
            )}
            <TabsTrigger value="products" className="flex items-center justify-center gap-2 py-2.5 text-xs sm:text-sm">
              <Package className="h-4 w-4 shrink-0" />
              Products
            </TabsTrigger>
          </TabsList>

          {canEditMargins && (
          <TabsContent value="margins" className="mt-0 space-y-8">
        <Card className="mb-8">
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="mb-0">Margin Tiers</CardTitle>
              {canEditMargins && (
                <div className="flex w-full shrink-0 flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap sm:justify-end">
                  <Button type="button" size="sm" className="w-full shrink-0 sm:w-auto" onClick={saveMargins} disabled={saving}>
                    <Save className="h-4 w-4 mr-2 shrink-0" /> {saving ? 'Saving...' : 'Save Margins'}
                  </Button>
                  <Button type="button" size="sm" className="w-full shrink-0 sm:w-auto" onClick={openPublishConfirm} disabled={publishing}>
                    {publishing ? <Loader2 className="h-4 w-4 mr-2 shrink-0 animate-spin" /> : <Rocket className="h-4 w-4 mr-2 shrink-0" />}
                    {publishing ? 'Publishing…' : 'Publish pricing'}
                  </Button>
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm">
              <div className="flex h-8 items-center gap-2">
                <span className="text-muted-foreground whitespace-nowrap">Markup %:</span>
                <Input
                  type="number"
                  step="0.1"
                  value={markupPct}
                  onChange={e => setMarkupPct(e.target.value)}
                  placeholder="e.g. 5"
                  className={cn('h-8 w-20 text-sm', inputNumberNoSpin)}
                  disabled={!canEditMargins}
                />
              </div>
              <div className="flex h-8 items-center gap-2">
                <span className="text-muted-foreground whitespace-nowrap">Sample cost:</span>
                <Input
                  type="number"
                  value={sampleCost}
                  onChange={e => setSampleCost(parseFloat(e.target.value) || 0)}
                  className={cn('h-8 w-28 text-sm', inputNumberNoSpin)}
                />
              </div>
              {sampleCost > 0 && markupN > 0 && (
                <div className="flex h-8 items-center gap-2">
                  <span className="text-muted-foreground whitespace-nowrap">RRP (buy-out):</span>
                  <span className="font-medium tabular-nums">{fmt(sampleCost * (1 + markupN / 100))}</span>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-auto max-h-[80vh]">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-background z-10">
                  <tr className="border-b">
                    <th className="text-left py-2 pr-3 pl-4 min-w-[80px]">Duration</th>
                    <th className="text-left py-2 pr-3 min-w-[7rem]">Margin %</th>
                    <th className="text-right py-2 pr-3 min-w-[100px]">Monthly</th>
                    <th className="text-right py-2 pr-3 min-w-[100px]">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {MARGIN_DURATIONS.map(dur => (
                    <MarginTierRow
                      key={dur}
                      dur={dur}
                      marginStr={margins[dur] ?? ''}
                      sampleCost={sampleCost}
                      canEditMargins={canEditMargins}
                      onMarginChange={onMarginChange}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="mb-8">
          <CardHeader>
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="mb-0">Profitability Planner</CardTitle>
              {canEditMargins && (
                <Button type="button" variant="secondary" size="sm" className="w-full shrink-0 sm:w-auto" onClick={applyPlannerToMargins}>
                  <Wand2 className="h-4 w-4 mr-2 shrink-0" />
                  Apply planner curve to all tier rows
                </Button>
              )}
            </div>
            {canEditMargins && (
              <div className="mt-6 flex flex-col gap-8 border-t border-border pt-6 lg:flex-row lg:items-stretch lg:gap-0">
                <div className="flex min-w-0 flex-col gap-3 lg:w-[11.5rem] lg:flex-none lg:shrink-0 lg:pr-6 xl:pr-8">
                  <span className="text-sm font-medium leading-tight text-muted-foreground">Sample cost (planner)</span>
                  <Input
                    type="number"
                    value={sampleCost}
                    onChange={e => setSampleCost(parseFloat(e.target.value) || 0)}
                    className={cn('h-8 w-full max-w-[9rem] text-sm', inputNumberNoSpin)}
                  />
                </div>

                <div className="hidden w-px shrink-0 bg-border lg:block" aria-hidden />

                <div className="flex min-w-0 flex-1 flex-col gap-3 border-t border-border pt-8 lg:border-t-0 lg:pt-0 lg:px-6 xl:px-8">
                  <span className="text-sm font-medium leading-tight">Break even in</span>
                  <PlannerSliderTrack
                    min={1}
                    max={MAX_BREAK_EVEN_TARGET_MO}
                    step={1}
                    value={breakEvenTargetMo}
                    onValueChange={n => setTargetBreakEven(String(n))}
                    ariaLabel="Break-even target months"
                    ariaValueText={`${breakEvenTargetMo} months`}
                    tooltipText={`${breakEvenTargetMo} mo`}
                    startLabel="1"
                    endLabel={String(MAX_BREAK_EVEN_TARGET_MO)}
                  />
                </div>

                <div className="hidden w-px shrink-0 bg-border lg:block" aria-hidden />

                <div className="flex min-w-0 flex-1 flex-col gap-3 border-t border-border pt-8 lg:border-t-0 lg:pt-0 lg:pl-6 xl:pl-8">
                  <span className="text-sm font-medium leading-tight">Long-term discount</span>
                  <PlannerSliderTrack
                    min={DISCOUNT_MIN}
                    max={DISCOUNT_MAX}
                    step={DISCOUNT_STEP}
                    value={curvePowerEffective}
                    onValueChange={n => setCurvePower(String(n))}
                    ariaLabel="Long-term discount curve"
                    ariaValueText={fmtCurveTooltip(curvePowerEffective)}
                    tooltipText={fmtCurveTooltip(curvePowerEffective)}
                    startLabel="Steep"
                    endLabel="Flat"
                  />
                </div>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {profitabilityPlan ? (
              <>
                <div className="mb-6 border rounded-md p-4">
                  <p className="text-xs text-muted-foreground mb-3">Monthly price curve by duration (sample cost)</p>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={profitabilityPlan} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                      <XAxis dataKey="duration" tickFormatter={v => `${v}mo`} tick={{ fontSize: 12 }} />
                      <YAxis tickFormatter={v => `£${v}`} tick={{ fontSize: 12 }} width={60} />
                      <RechartsTooltip
                        formatter={(value: number) => [fmt(value), 'Monthly']}
                        labelFormatter={l => `${l} month${Number(l) > 1 ? 's' : ''}`}
                      />
                      <Line
                        type="monotone"
                        dataKey="monthly_price"
                        stroke="hsl(var(--primary))"
                        strokeWidth={2}
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2">Duration</th>
                      <th className="text-right py-2">Current Margin</th>
                      <th className="text-right py-2">Required Margin</th>
                      <th className="text-right py-2">Monthly Price</th>
                      <th className="text-right py-2">Total Revenue</th>
                      <th className="text-right py-2">Profit/Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {profitabilityPlan.map(row => (
                      <tr key={row.duration} className="border-b last:border-0">
                        <td className="py-1.5">{row.duration} month{row.duration > 1 ? 's' : ''}</td>
                        <td className={`py-1.5 text-right font-mono ${row.current_margin !== null ? '' : 'text-muted-foreground'}`}>
                          {row.current_margin !== null ? `${row.current_margin}%` : '—'}
                        </td>
                        <td className="py-1.5 text-right font-mono">{row.required_margin.toFixed(1)}%</td>
                        <td className="py-1.5 text-right font-mono">{fmt(row.monthly_price)}</td>
                        <td className="py-1.5 text-right font-mono">{fmt(row.total_revenue)}</td>
                        <td className={`py-1.5 text-right font-mono ${row.profit_at_end >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {row.profit_at_end >= 0 ? '+' : ''}{fmt(row.profit_at_end)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Set Sample Cost (greater than zero) to use the planner.</p>
            )}
          </CardContent>
        </Card>
          </TabsContent>
          )}

          {canEditMargins && (
          <TabsContent value="history" className="mt-0 space-y-6">
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Pricing publish history</CardTitle>
            <CardDescription>
              Async jobs that saved margin tiers and recalculated rental prices. Select a row to load the full snapshot below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {pricingEvents.length === 0 ? (
              <p className="text-sm text-muted-foreground">No publishes yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-2 pr-3">When</th>
                      <th className="text-left py-2 pr-3">Status</th>
                      <th className="text-right py-2 pr-3">Recalc</th>
                      <th className="text-right py-2 pr-3">Skip</th>
                      <th className="text-right py-2 pr-3">Fail</th>
                      <th className="text-left py-2">By</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pricingEvents.map(ev => (
                      <tr
                        key={ev.id}
                        className={cn(
                          'border-b last:border-0 cursor-pointer hover:bg-muted/50',
                          selectedHistoryEventId === ev.id && 'bg-muted/70',
                        )}
                        tabIndex={0}
                        role="button"
                        aria-label="View publish snapshot"
                        onClick={() => setSelectedHistoryEventId(ev.id)}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setSelectedHistoryEventId(ev.id);
                          }
                        }}
                      >
                        <td className="py-1.5 pr-3 whitespace-nowrap">{new Date(ev.created_at).toLocaleString()}</td>
                        <td className="py-1.5 pr-3"><Badge variant="secondary">{ev.status}</Badge></td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{ev.templates_recalculated}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{ev.templates_skipped}</td>
                        <td className="py-1.5 pr-3 text-right tabular-nums">{ev.templates_failed}</td>
                        <td className="py-1.5 text-muted-foreground truncate max-w-[180px]">{ev.actor_user?.email ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {!selectedHistoryEventId ? (
          <Card>
            <CardContent className="pt-6">
              <p className="text-sm text-muted-foreground">
                Select a publish row above to view stored margins, rental totals, and apply a previous curve on the Margins tab.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div ref={historySnapshotRef} className="space-y-3">
            <h3 className="text-lg font-heading font-semibold">Snapshot</h3>
            <PublishSnapshotPanel
              loading={historyDetailLoading}
              detail={historyDetail}
              navigate={navigate}
              canEditMargins={canEditMargins}
              onApplyPrevious={applyPreviousMarginsFromHistory}
            />
          </div>
        )}
          </TabsContent>
          )}

          <TabsContent value="products" className="mt-0 space-y-6">
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Products from {supplier.name}</CardTitle>
                <CardDescription>{supplier.products.length} products</CardDescription>
              </div>
              <Input
                placeholder="Search products..."
                value={productSearch}
                onChange={e => { setProductSearch(e.target.value); setProductPage(1); }}
                className="w-64 h-9 text-sm"
              />
            </div>
          </CardHeader>
          <CardContent>
            {supplier.products.length === 0 ? (
              <p className="text-muted-foreground text-sm py-4">No products linked to this supplier yet.</p>
            ) : (() => {
              const filtered = supplier.products.filter(p =>
                p.name.toLowerCase().includes(productSearch.toLowerCase())
              );
              const totalPages = Math.ceil(filtered.length / PRODUCTS_PER_PAGE);
              const paginated = filtered.slice((productPage - 1) * PRODUCTS_PER_PAGE, productPage * PRODUCTS_PER_PAGE);

              return (
                <>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left py-2">Name</th>
                        <th className="text-right py-2">Cost Price</th>
                        <th className="text-right py-2">RRP (buy-out)</th>
                        <th className="text-center py-2">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map(p => {
                        const cost = Number(p.cost_price || 0);
                        const markup = parseFloat(markupPct) || 0;
                        const rrp = cost > 0 && markup > 0 ? cost * (1 + markup / 100) : null;
                        return (
                          <tr
                            key={p.id}
                            className="border-b last:border-0 hover:bg-muted/50 cursor-pointer"
                            onClick={() => navigate(`/assets/${p.id}`)}
                          >
                            <td className="py-2 text-primary hover:underline">
                              <div className="flex items-center gap-3">
                                {p.images?.[0]?.url ? (
                                  <ProductThumbnail src={p.images[0].url} alt={p.name} size="sm" />
                                ) : (
                                  <ProductThumbnail src={null} alt={p.name} size="sm" />
                                )}
                                <span>{p.name}</span>
                              </div>
                            </td>
                            <td className="py-2 text-right font-mono">{cost > 0 ? fmt(cost) : '—'}</td>
                            <td className="py-2 text-right font-mono">{rrp ? fmt(rrp) : '—'}</td>
                            <td className="py-2 text-center">
                              <Badge variant={p.is_active ? 'default' : 'secondary'}>
                                {p.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  {filtered.length === 0 && (
                    <p className="text-muted-foreground text-sm py-4 text-center">No products match "{productSearch}"</p>
                  )}
                  {totalPages > 1 && (
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-sm text-muted-foreground">
                        Showing {(productPage - 1) * PRODUCTS_PER_PAGE + 1}–{Math.min(productPage * PRODUCTS_PER_PAGE, filtered.length)} of {filtered.length}
                        {productSearch && ` (filtered from ${supplier.products.length})`}
                      </span>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" disabled={productPage <= 1} onClick={() => setProductPage(p => p - 1)}>
                          Previous
                        </Button>
                        <span className="text-sm flex items-center px-2">Page {productPage} of {totalPages}</span>
                        <Button variant="outline" size="sm" disabled={productPage >= totalPages} onClick={() => setProductPage(p => p + 1)}>
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
          </CardContent>
        </Card>
          </TabsContent>
        </Tabs>

        <AlertDialog open={publishConfirmOpen} onOpenChange={setPublishConfirmOpen}>
          <AlertDialogContent className="max-w-lg">
            <AlertDialogHeader>
              <AlertDialogTitle>Publish pricing?</AlertDialogTitle>
            </AlertDialogHeader>
            <div className="max-h-[min(60vh,28rem)] space-y-4 overflow-y-auto pr-1 text-sm text-muted-foreground">
              <p>
                Margin tiers from this screen will be saved and rental calendar pricing recalculated for{' '}
                <span className="font-medium text-foreground">{publishPreview.eligible.length}</span> product
                {publishPreview.eligible.length === 1 ? '' : 's'} (of {supplier.products.length} listed for this supplier).
              </p>
              {publishPreview.markupMismatch && (
                <p className="text-amber-600 dark:text-amber-400">
                  Saved markup % ({Number(supplier.markup_percentage ?? 0)}) differs from the field above. The server uses
                  saved values for RRP until you click Save Margins.
                </p>
              )}
              {publishPreview.eligible.length > 0 && (
                <div>
                  <p className="mb-2 font-medium text-foreground">Products to recalculate</p>
                  <ul className="list-disc space-y-0.5 pl-5 text-foreground">
                    {publishPreview.eligible.map(p => (
                      <li key={p.id}>{p.name}</li>
                    ))}
                  </ul>
                </div>
              )}
              {publishPreview.eligible.length === 0 && publishPreview.skipped.length > 0 && (
                <p className="text-foreground">No products qualify for recalculation with current saved markup and costs; tiers will still be updated.</p>
              )}
              {publishPreview.skipped.length > 0 && (
                <div>
                  <p className="mb-2 font-medium text-foreground">
                    Skipped ({publishPreview.skipped.length}) — no rental recalculation
                  </p>
                  <ul className="list-disc space-y-1 pl-5 text-xs">
                    {publishPreview.skipped.slice(0, 25).map(({ product, reason }) => (
                      <li key={product.id}>
                        {product.name}
                        <span className="text-muted-foreground"> — {reason}</span>
                      </li>
                    ))}
                  </ul>
                  {publishPreview.skipped.length > 25 && (
                    <p className="mt-1 text-xs text-muted-foreground">
                      …and {publishPreview.skipped.length - 25} more
                    </p>
                  )}
                </div>
              )}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel type="button">Cancel</AlertDialogCancel>
              <Button type="button" onClick={() => void executePublish()} disabled={publishing}>
                {publishing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Confirm publish
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        <MessageDialog
          open={resultDialog.open}
          onOpenChange={open => setResultDialog(d => ({ ...d, open }))}
          title={resultDialog.title}
          description={resultDialog.description}
          variant={resultDialog.destructive ? 'destructive' : 'default'}
        />
      </main>
    </div>
  );
}
